import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faChevronDown, faChevronRight, faLocationDot, faPersonSwimming, faCloudDownload, faVenusMars } from '@fortawesome/free-solid-svg-icons';
import { useSwimmerAPI } from '../context/SwimmerAPIContext';
import { compareEvents, sortStrokeValues, parseEventName } from '../../services/utils/EventOrdering';
import { COUNTIES, detectCountyCode } from '../../services/utils/counties';
import { REGIONS } from '../../services/utils/regions';
import { getCountyCodeForClub, getRegionCodeForClub } from '../../services/utils/clubs';
import { getAgeGroup, AGE_GROUPS } from '../../services/utils/ageCalculation';
import { useCollapsible } from '../hooks/useCollapsible';
import AgeGroupMultiSelect from './controls/AgeGroupMultiSelect';
import { SegmentedToggle } from './filters';
const LEVEL_OPTIONS = [
    { value: 'N', label: 'National' },
    { value: 'R', label: 'Regional' },
    { value: 'C', label: 'County' },
];
const RANKING_MODE_OPTIONS = [
    { value: 'average', label: 'by Avg FINA' },
    { value: 'percentage', label: 'by % Share' },
];
function getDefaultSex(gender) {
    return gender?.toUpperCase().includes('FEMALE') ? 'F' : 'M';
}
function aggregateByClub(lcEntries, scEntries) {
    const tagged = [
        ...lcEntries.map(e => ({ ...e, course: '50m' })),
        ...scEntries.map(e => ({ ...e, course: '25m' })),
    ];
    const map = new Map();
    let grandTotal = 0;
    for (const entry of tagged) {
        const fina = parseFloat(entry.fina);
        if (!entry.club || isNaN(fina) || fina <= 0)
            continue;
        if (!map.has(entry.club))
            map.set(entry.club, { total: 0, count: 0, swimmers: [] });
        const c = map.get(entry.club);
        c.total += fina;
        c.count++;
        grandTotal += fina;
        c.swimmers.push({ name: entry.name, fina: entry.fina, position: entry.position, course: entry.course });
    }
    return Array.from(map.entries()).map(([club, d]) => ({
        club,
        avgFina: d.total / d.count,
        pctFina: grandTotal > 0 ? (d.total / grandTotal) * 100 : 0,
        swimmerCount: d.count,
        swimmers: d.swimmers,
    }));
}
const ClubLeaderboard = ({ swimmerData, loading, setLoading, showToast }) => {
    const api = useSwimmerAPI();
    const uniqueEvents = Array.from(new Set(swimmerData.times.map(t => t.event)))
        .sort((a, b) => compareEvents(a, b));
    const [selectedEvent, setSelectedEvent] = useState(uniqueEvents[0] ?? '');
    const [level, setLevel] = useState('N');
    const [countyCode, setCountyCode] = useState(swimmerData.selectedCountyCode ?? getCountyCodeForClub(swimmerData.club) ?? detectCountyCode(swimmerData.region));
    const [regionCode, setRegionCode] = useState(swimmerData.selectedRegionCode ?? getRegionCodeForClub(swimmerData.club) ?? REGIONS[2].code);
    const [selectedAges, setSelectedAges] = useState(() => {
        const group = getAgeGroup(swimmerData.birthYear);
        return group ? [group.age] : [];
    });
    const [selectedSex, setSelectedSex] = useState(() => getDefaultSex(swimmerData.gender));
    const [rankingMode, setRankingMode] = useState('percentage');
    const [leaderboard, setLeaderboard] = useState([]);
    const [fetched, setFetched] = useState(false);
    const [expandedClubs, toggleClub, resetExpandedClubs] = useCollapsible();
    // Reset defaults whenever the selected swimmer changes.
    // NOTE: swap `swimmerData.id` for whichever field uniquely identifies a swimmer in your SwimmerData type.
    useEffect(() => {
        const group = getAgeGroup(swimmerData.birthYear);
        setSelectedAges(group ? [group.age] : []);
        setSelectedSex(getDefaultSex(swimmerData.gender));
        setFetched(false);
        resetExpandedClubs();
        setLeaderboard([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [swimmerData.tiref]);
    const selectedCountyName = COUNTIES.find(c => c.code === countyCode)?.name ?? countyCode;
    const selectedRegionName = REGIONS.find(c => c.code === regionCode)?.name ?? regionCode;
    const fetchLeaderboard = async () => {
        if (!swimmerData.birthYear || !swimmerData.gender) {
            showToast('Cannot load leaderboard: missing birth year or gender', 'error');
            return;
        }
        if (!selectedEvent) {
            showToast('Please select an event', 'error');
            return;
        }
        if (selectedAges.length === 0) {
            showToast('Please select at least one age group', 'error');
            return;
        }
        setLoading(true);
        try {
            // Fetch each selected age group individually and merge - no implicit
            // combining; the user chooses which ages to include.
            const responses = await Promise.all(selectedAges.map(age => api.getClubLeaderboard({
                event: selectedEvent,
                ageGroup: age,
                sex: selectedSex,
                level,
                countyCode,
                regionCode,
            })));
            const lcEntries = responses.flatMap(r => r.lcEntries);
            const scEntries = responses.flatMap(r => r.scEntries);
            const result = aggregateByClub(lcEntries, scEntries);
            setLeaderboard(result);
            setFetched(true);
            resetExpandedClubs();
            if (result.length === 0) {
                showToast('No club data found for this event', 'error');
            }
        }
        catch (error) {
            showToast(`Error loading leaderboard: ${error.message}`, 'error');
        }
        finally {
            setLoading(false);
        }
    };
    const groupedByStroke = uniqueEvents.reduce((acc, evt) => {
        const { stroke } = parseEventName(evt);
        if (!acc[stroke])
            acc[stroke] = [];
        acc[stroke].push(evt);
        return acc;
    }, {});
    const sortedStrokes = sortStrokeValues(Object.keys(groupedByStroke));
    return (React.createElement("div", { className: "tab-content active" },
        React.createElement("div", { className: "filter-section" },
            React.createElement("div", { className: "rankings-county-filter" },
                React.createElement(FontAwesomeIcon, { icon: faPersonSwimming, className: "rankings-county-icon" }),
                React.createElement("select", { value: selectedEvent, onChange: e => { setSelectedEvent(e.target.value); setFetched(false); }, disabled: loading, className: "rankings-county-select", "aria-label": "Event" }, sortedStrokes.map(stroke => (React.createElement("optgroup", { key: stroke, label: stroke }, groupedByStroke[stroke].map(evt => (React.createElement("option", { key: evt, value: evt }, evt)))))))),
            React.createElement(AgeGroupMultiSelect, { options: AGE_GROUPS, selected: selectedAges, onChange: ages => { setSelectedAges(ages); setFetched(false); }, disabled: loading }),
            React.createElement("div", { className: "rankings-county-filter" },
                React.createElement(FontAwesomeIcon, { icon: faVenusMars, className: "rankings-county-icon" }),
                React.createElement("select", { value: selectedSex, onChange: e => { setSelectedSex(e.target.value); setFetched(false); }, disabled: loading, className: "rankings-county-select", "aria-label": "Sex" },
                    React.createElement("option", { value: "M" }, "Male"),
                    React.createElement("option", { value: "F" }, "Female"))),
            React.createElement("div", { className: "filter-chip-group" },
                React.createElement(SegmentedToggle, { options: LEVEL_OPTIONS, value: level, onChange: next => { setLevel(next); setFetched(false); }, disabled: loading })),
            level === 'C' && (React.createElement("div", { className: "rankings-county-filter" },
                React.createElement(FontAwesomeIcon, { icon: faLocationDot, className: "rankings-county-icon" }),
                React.createElement("select", { value: countyCode, onChange: e => { setCountyCode(e.target.value); setFetched(false); }, disabled: loading, className: "rankings-county-select", "aria-label": "County" }, COUNTIES.map(c => (React.createElement("option", { key: c.code, value: c.code }, c.name)))))),
            level === 'R' && (React.createElement("div", { className: "rankings-county-filter" },
                React.createElement(FontAwesomeIcon, { icon: faLocationDot, className: "rankings-county-icon" }),
                React.createElement("select", { value: regionCode, onChange: e => { setRegionCode(e.target.value); setFetched(false); }, disabled: loading, className: "rankings-county-select", "aria-label": "Region" }, REGIONS.map(c => (React.createElement("option", { key: c.code, value: c.code }, c.name)))))),
            React.createElement("div", { style: { alignSelf: 'center', marginLeft: 'auto' } },
                React.createElement("button", { className: "btn-fetch", onClick: fetchLeaderboard, disabled: loading || !selectedEvent || selectedAges.length === 0, style: { padding: '0.35rem 0.75rem', fontSize: '0.85rem' } }, loading ? React.createElement(React.Fragment, null,
                    React.createElement(FontAwesomeIcon, { icon: faSpinner, spin: true }),
                    " Fetching\u2026") : React.createElement(React.Fragment, null,
                    React.createElement(FontAwesomeIcon, { icon: faCloudDownload }),
                    "\u00A0Fetch")))),
        fetched && leaderboard.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement("div", { style: { padding: '0.5rem 0 0.75rem', fontSize: '0.8rem', color: 'var(--gray-400)' } },
                selectedEvent,
                " \u00B7 ",
                level === 'N' ? 'National' : level === 'R' ? selectedRegionName + ' Region' : selectedCountyName + ' County',
                " \u00B7 LC + SC combined",
                selectedAges.length > 0 && ` · Age${selectedAges.length > 1 ? 's' : ''} ${selectedAges
                    .map(a => AGE_GROUPS.find(g => g.age === a)?.displayAge ?? String(a))
                    .join(', ')}`,
                ' · ',
                selectedSex === 'F' ? 'Female' : 'Male',
                ' · ',
                leaderboard.length,
                " clubs"),
            React.createElement("div", { className: "filter-chip-group", style: { marginBottom: '1rem' } },
                React.createElement(SegmentedToggle, { options: RANKING_MODE_OPTIONS, value: rankingMode, onChange: setRankingMode, disabled: loading })),
            React.createElement("div", { className: "table-container" },
                React.createElement("table", null,
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("th", null, "Club"),
                            React.createElement("th", { className: "hide-on-mobile", style: { textAlign: 'right' } }, rankingMode === 'average' ? 'Avg FINA' : '% Share'),
                            React.createElement("th", { className: "hide-on-mobile", style: { textAlign: 'right' } }, "Swims"))),
                    React.createElement("tbody", null, [...leaderboard]
                        .sort((a, b) => rankingMode === 'average' ? b.avgFina - a.avgFina : b.pctFina - a.pctFina)
                        .map((entry, index) => (React.createElement(React.Fragment, { key: entry.club },
                        React.createElement("tr", { onClick: () => toggleClub(entry.club), style: { cursor: 'pointer' } },
                            React.createElement("td", { className: "card-event-td" },
                                React.createElement("div", { className: "card-event-header" },
                                    React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '0.5rem' } },
                                        React.createElement("strong", { style: { color: 'var(--gray-400)', minWidth: '1.5rem', fontSize: '0.85rem' } }, index + 1),
                                        React.createElement(FontAwesomeIcon, { icon: expandedClubs.has(entry.club) ? faChevronDown : faChevronRight, style: { fontSize: '0.7rem', color: 'var(--gray-400)', flexShrink: 0 } }),
                                        React.createElement("span", null, entry.club)),
                                    React.createElement("span", { className: "mobile-supplement card-inline-time" }, rankingMode === 'average' ? entry.avgFina.toFixed(1) : `${entry.pctFina.toFixed(2)}%`)),
                                React.createElement("div", { className: "mobile-supplement card-meta-row", style: { color: 'var(--gray-400)' } },
                                    entry.swimmerCount,
                                    " swim",
                                    entry.swimmerCount !== 1 ? 's' : '',
                                    " \u00B7 tap to expand")),
                            React.createElement("td", { "data-label": rankingMode === 'average' ? 'Avg FINA' : '% Share', className: "hide-on-mobile", style: { textAlign: 'right' } },
                                React.createElement("strong", null, rankingMode === 'average' ? entry.avgFina.toFixed(1) : `${entry.pctFina.toFixed(2)}%`)),
                            React.createElement("td", { "data-label": "Swimmers", className: "hide-on-mobile", style: { textAlign: 'right' } }, entry.swimmerCount)),
                        expandedClubs.has(entry.club) && (React.createElement("tr", null,
                            React.createElement("td", { colSpan: 3, className: "card-event-td", style: { paddingTop: '0.1rem', paddingBottom: '0.6rem' } }, entry.swimmers
                                .sort((a, b) => parseFloat(b.fina) - parseFloat(a.fina))
                                .map((swimmer, si) => (React.createElement("div", { key: si, style: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.2rem 0',
                                    fontSize: '0.82rem',
                                    borderTop: si > 0 ? '1px solid var(--gray-100)' : undefined,
                                } },
                                React.createElement("span", { style: { flex: 1 } }, swimmer.name),
                                React.createElement("span", { style: { color: 'var(--gray-400)' } },
                                    swimmer.fina,
                                    " pts"),
                                React.createElement("span", { style: { color: 'var(--gray-400)' } },
                                    "#",
                                    swimmer.position),
                                React.createElement("span", { className: "swimmer-tag-badge" }, swimmer.course === '50m' ? 'LC' : 'SC'))))))))))))))),
        fetched && leaderboard.length === 0 && (React.createElement("div", { className: "empty-state" },
            React.createElement("p", null, "No club data found for this event and level combination"))),
        !fetched && (React.createElement("div", { style: { marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--card-bg)', borderRadius: '4px' } },
            React.createElement("p", { style: { fontSize: '0.9rem', color: 'var(--gray-400)', margin: 0 } },
                React.createElement("strong", null, "Club Leaderboard"),
                " ranks clubs by average or % share of FINA points from the top 100 swimmers for the selected event, combining Long Course and Short Course results. Select an event and click ",
                React.createElement("strong", null, "Fetch"),
                " to load.")))));
};
export default ClubLeaderboard;
